import { FC, useContext, useEffect, useState } from "react";
import {
  GroupBase,
  MultiValueProps,
  OptionsOrGroups,
  SingleValueProps,
} from "react-select";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { format, parse, subDays } from "date-fns";
import DatePicker from "react-datepicker";
import ReactInputMask from "react-input-mask";
import { useDebouncedCallback } from "use-debounce";
import { limitShift, offset, shift } from "@floating-ui/react";

import { AuthContext } from "src/context/authContext";
import useFetch from "src/hooks/useFetch";

import { APIEndpoints } from "src/types/apiTypes";
import HubInputField from "src/components/inputType";
import { StatisticsProps } from "./types";

import {
  CasDropdownResponseType,
  PatientFilterState,
  TerritoryDropdownResponseType,
} from "src/pages/actionDashboard/types";

import { OverlayTrigger, Tooltip } from "react-bootstrap";

const ActionDashBoardFilter: FC<StatisticsProps> = ({
  updateQueryParams,
  defaultQueryParams,
  region_dropdown,
  rsd_dropdown,
  activeTab,
  territory_dropdown,
  state_dropdown,
}): JSX.Element => {
  type Option = {
    label: string;
    value: string;
  };

  type OptionType = {
    label: string;
    value: string;
  };

  /*
   * ============================================================
   * LOCAL DROPDOWN DATA
   * ============================================================
   *
   * Important:
   * Territory and State are initialized with the FULL dropdowns
   * received from props.
   *
   * This means:
   *
   * No Region selected
   *      -> all Territories available
   *
   * No Territory selected
   *      -> all States available
   */
  const [territoryDropdown, updateTerritoryDropdown] = useState<string[]>(
    territory_dropdown ?? []
  );

  const [stateDropdown, updateStateDropdown] = useState<string[]>(
    state_dropdown ?? []
  );

  const [casDropdown, updateCasDropdown] = useState<
    { id: number; name: string }[]
  >([]);

  const { userData } = useContext(AuthContext);

  /*
   * ============================================================
   * FORM
   * ============================================================
   */
  const {
    control,
    handleSubmit,
    reset,
    resetField,
    setValue,
  } = useForm<PatientFilterState>({
    mode: "all",

    defaultValues: {
      ...defaultQueryParams,
      drug_id: "",
    },
  });

  const today = new Date();
  const hundredDaysAgo = subDays(today, 100);

  /*
   * We only need to watch dates now.
   *
   * The previous Region/Territory watch effects were removed
   * because dependency handling is now done directly inside
   * their onChange handlers.
   */
  const start_date = control._getWatch("start_date");
  const end_date = control._getWatch("end_date");

  /*
   * ============================================================
   * SYNC FULL TERRITORY / STATE DROPDOWNS FROM PROPS
   * ============================================================
   *
   * If parent component receives new full dropdown data,
   * update our local full list.
   */
  useEffect(() => {
    updateTerritoryDropdown(territory_dropdown ?? []);
  }, [territory_dropdown]);

  useEffect(() => {
    updateStateDropdown(state_dropdown ?? []);
  }, [state_dropdown]);

  /*
   * ============================================================
   * TOOLTIP COMPONENTS
   * ============================================================
   */
  const SingleValueWithTooltip = (props: SingleValueProps<any>) => {
    const { data } = props;

    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{data?.label}</Tooltip>}
      >
        <span className="tooltip-value">{data?.label}</span>
      </OverlayTrigger>
    );
  };

  const MultiValueWithTooltip = (
    props: MultiValueProps<OptionType, true, GroupBase<OptionType>>
  ) => {
    const { data, removeProps } = props;

    return (
      <div className="multi-select-field">
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{data.label}</Tooltip>}
        >
          <span className="multi-select__label">
            {data.label}
          </span>
        </OverlayTrigger>

        <span
          className="multi-select-remover"
          {...removeProps}
        >
          ×
        </span>
      </div>
    );
  };

  /*
   * ============================================================
   * TERRITORY DROPDOWN API
   * ============================================================
   *
   * Called when Region is selected.
   */
  const {
    data: territoryDropdownData,
    error: territoryDropdownError,
    mutate: territoryDropdownMutate,
  } = useFetch<TerritoryDropdownResponseType>(
    APIEndpoints.TERRITORY_DROPDOWN,
    {},
    {},
    false
  );

  useEffect(() => {
    if (territoryDropdownData || territoryDropdownError) {
      if (
        territoryDropdownData &&
        territoryDropdownData.data
      ) {
        updateTerritoryDropdown(
          territoryDropdownData.data
        );
      }
    }
  }, [
    territoryDropdownData,
    territoryDropdownError,
  ]);

  /*
   * ============================================================
   * STATE DROPDOWN API
   * ============================================================
   *
   * Called when Territory is selected.
   */
  const {
    data: stateDropdownData,
    error: stateDropdownError,
    mutate: stateDropdownMutate,
  } = useFetch<TerritoryDropdownResponseType>(
    APIEndpoints.STATE_DROPDOWN,
    {},
    {},
    false
  );

  useEffect(() => {
    if (stateDropdownData || stateDropdownError) {
      if (
        stateDropdownData &&
        stateDropdownData.data
      ) {
        updateStateDropdown(stateDropdownData.data);
      }
    }
  }, [
    stateDropdownData,
    stateDropdownError,
  ]);

  /*
   * ============================================================
   * CAS DROPDOWN
   * ============================================================
   */
  const {
    data: casDropdownData,
    error: casDropdownError,
    mutate: casDropdownMutate,
  } = useFetch<CasDropdownResponseType>(
    APIEndpoints.CAS_DROPDOWN,
    {},
    {},
    false
  );

  useEffect(() => {
    if (casDropdownData || casDropdownError) {
      if (
        casDropdownData &&
        casDropdownData.data
      ) {
        updateCasDropdown(casDropdownData.data);
      }
    }
  }, [casDropdownData, casDropdownError]);

  const fetchCasDropdown = (
    rsdValues?: string[]
  ) => {
    casDropdownMutate(
      {},
      {},
      rsdValues && rsdValues.length > 0
        ? {
            ...defaultQueryParams,
            rsd: rsdValues,
          }
        : {
            ...defaultQueryParams,
            rsd: [],
          }
    );
  };

  useEffect(() => {
    fetchCasDropdown();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * ============================================================
   * PRACTICE SEARCH
   * ============================================================
   */
  const {
    data: practiceListing,
    mutate: getPracticeListing,
  } = useFetch<{ id: number; name: string }[]>(
    APIEndpoints.AD_PRACTICE_DROPDOWN,
    {},
    {},
    false
  );

  const filterPractice = (search: string) => {
    if (search.length >= 2) {
      getPracticeListing(
        {},
        {},
        {
          query: search,
        }
      );
    }
  };

  const debouncedFilterPractice =
    useDebouncedCallback(
      filterPractice,
      500
    );

  /*
   * ============================================================
   * PROVIDER SEARCH
   * ============================================================
   */
  const {
    data: providersListing,
    mutate: getProvidersListing,
  } = useFetch<{ id: number; name: string }[]>(
    APIEndpoints.AD_PROVIDER_DROPDOWN,
    {},
    {},
    false
  );

  const filterDoctor = (search: string) => {
    if (search.length >= 2) {
      getProvidersListing(
        {},
        {},
        {
          query: search,
        }
      );
    }
  };

  const debouncedFilterDoctor =
    useDebouncedCallback(filterDoctor, 500);

  /*
   * ============================================================
   * HELPERS
   * ============================================================
   */
  const normalizeToArray = (
    value?: string | string[]
  ) => {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value;
    }

    return value
      .split(",")
      .map((value) => value.trim());
  };

  const resolveMultiSelectValue = (
    value: unknown,
    dropdown?: {
      id: number;
      name: string;
    }[]
  ): {
    label: string;
    value: string;
  }[] => {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value as {
        label: string;
        value: string;
      }[];
    }

    return (value as string)
      .split("&")
      .map((val: string) => {
        const match = dropdown?.find(
          (item) =>
            item.id.toString() === val
        );

        return {
          label: match ? match.name : val,
          value: val,
        };
      });
  };

  /*
   * ============================================================
   * SEARCH
   * ============================================================
   */
  const searchForStats = (
    values: PatientFilterState
  ) => {
    const updatedValues = {
      ...defaultQueryParams,
      ...values,

      global_doctor_id:
        values.global_doctor_id
          ? typeof values.global_doctor_id ===
            "string"
            ? values.global_doctor_id
            : (
                values.global_doctor_id as unknown as {
                  value: string;
                  label: string;
                }
              ).value
          : "",

      drug_id: values.drug_id
        ? typeof values.drug_id === "string"
          ? values.drug_id === "All Products"
            ? ""
            : values.drug_id
          : (
              values.drug_id as {
                value: string;
                label: string;
              }
            ).value === "All Products"
          ? ""
          : (
              values.drug_id as {
                value: string;
                label: string;
              }
            ).value
        : "",

      practice_id: values.practice_id
        ? typeof values.practice_id === "string"
          ? values.practice_id
          : (
              values.practice_id as unknown as {
                value: string;
                label: string;
              }
            ).value
        : "",

      region: values.region
        ? typeof values.region === "string"
          ? values.region
          : (
              values.region as unknown as {
                value: string;
                label: string;
              }
            ).value
        : "",

      territory: values.territory
        ? typeof values.territory === "string"
          ? values.territory
          : (
              values.territory as unknown as {
                value: string;
                label: string;
              }
            ).value
        : "",

      state: values.state
        ? typeof values.state === "string"
          ? values.state
          : (
              values.state as unknown as {
                value: string;
                label: string;
              }
            ).value
        : "",

      cas: values.cas
        ? typeof values.cas === "string"
          ? values.cas
          : (
              values.cas as unknown as {
                value: string;
                label: string;
              }[]
            )
              .map((opt) => opt.value)
              .join("&")
        : "",

      rsd: values.rsd
        ? typeof values.rsd === "string"
          ? values.rsd
          : (
              values.rsd as unknown as {
                value: string;
                label: string;
              }[]
            )
              .map((opt) => opt.value)
              .join("&")
        : "",
    };

    /*
     * ========================================================
     * DRUG LOGIC
     * ========================================================
     */
    const regionValues = normalizeToArray(
      updatedValues.region
    ).map((value) => value.toLowerCase());

    const territoryValues = normalizeToArray(
      updatedValues.territory
    ).map((value) => value.toLowerCase());

    const allGeoValues = [
      ...regionValues,
      ...territoryValues,
    ];

    if (allGeoValues.length > 0) {
      const hasZ = allGeoValues.some((value) =>
        value.startsWith("z")
      );

      const hasJ = allGeoValues.some((value) =>
        value.startsWith("j")
      );

      const hasI = allGeoValues.some((value) =>
        value.startsWith("i")
      );

      if (
        (hasZ || hasJ) &&
        !hasI
      ) {
        updatedValues.drug_id = "zilretta";
      } else if (
        hasI &&
        !(hasZ || hasJ)
      ) {
        updatedValues.drug_id = "iovera";
      } else {
        updatedValues.drug_id = "";
      }
    }

    console.log(
      "updatedValues....in filter........",
      updatedValues
    );

    updateQueryParams(
      updatedValues,
      false
    );
  };

  /*
   * ============================================================
   * CLEAR FORM
   * ============================================================
   */
  const clearFormData = () => {
    const values: any = {
      ...defaultQueryParams,

      start_date: activeTab.includes(
        "copay_claims"
      )
        ? format(
            new Date().setDate(
              new Date().getDate() - 90
            ),
            "yyyy-MM-dd"
          )
        : format(
            new Date().setDate(
              new Date().getDate() - 30
            ),
            "yyyy-MM-dd"
          ),

      end_date: format(
        new Date().setDate(
          new Date().getDate() + 1
        ),
        "yyyy-MM-dd"
      ),

      practice_id: "",
      global_doctor_id: "",
      case_id: "",
      global_patient_id: "",

      region: "",
      territory: "",
      state: "",

      cas: "",
      rsd: "",
      drug_id: "",
    };

    /*
     * Restore ALL dropdown values.
     */
    updateTerritoryDropdown(
      territory_dropdown ?? []
    );

    updateStateDropdown(
      state_dropdown ?? []
    );

    updateCasDropdown([]);

    fetchCasDropdown();

    reset(values);

    updateQueryParams(values, true);
  };

  return (
    <form
      className="row g-2 statistics-filter"
      onSubmit={handleSubmit(
        searchForStats
      )}
    >
      {/* ======================================================
          START DATE
      ====================================================== */}

      <div className="col date-field mb-2">
        <Controller
          name="start_date"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <DatePicker
              selected={
                field.value &&
                field.value.length > 0
                  ? parse(
                      field.value,
                      "yyyy-MM-dd",
                      new Date()
                    )
                  : null
              }
              onChange={(date) =>
                date
                  ? field.onChange(
                      format(
                        date,
                        "yyyy-MM-dd"
                      )
                    )
                  : field.onChange("")
              }
              customInput={
                <ReactInputMask mask="99/99/9999">
                  {(
                    inputProps: {} = {
                      className:
                        "form-control",
                    }
                  ) => (
                    <input
                      {...inputProps}
                    />
                  )}
                </ReactInputMask>
              }
              className="form-control"
              wrapperClassName="w-100"
              placeholderText="mm/dd/yyyy"
              dateFormat="MM/dd/yyyy"
              showMonthDropdown
              showYearDropdown
              maxDate={
                end_date
                  ? new Date(end_date)
                  : today
              }
              portalId="root"
              popperModifiers={[
                offset(8),
                shift({
                  limiter: limitShift(),
                }),
              ]}
            />
          )}
        />
      </div>

      {/* ======================================================
          END DATE
      ====================================================== */}

      <div className="col date-field mb-2">
        <Controller
          name="end_date"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <DatePicker
              selected={
                field.value &&
                field.value.length > 0
                  ? parse(
                      field.value,
                      "yyyy-MM-dd",
                      new Date()
                    )
                  : null
              }
              onChange={(date) =>
                date
                  ? field.onChange(
                      format(
                        date,
                        "yyyy-MM-dd"
                      )
                    )
                  : field.onChange("")
              }
              customInput={
                <ReactInputMask mask="99/99/9999">
                  {(
                    inputProps: {} = {
                      className:
                        "form-control",
                    }
                  ) => (
                    <input
                      {...inputProps}
                    />
                  )}
                </ReactInputMask>
              }
              className="form-control"
              wrapperClassName="w-100"
              placeholderText="mm/dd/yyyy"
              dateFormat="MM/dd/yyyy"
              showMonthDropdown
              showYearDropdown
              minDate={
                start_date
                  ? new Date(start_date)
                  : hundredDaysAgo
              }
              maxDate={today}
              portalId="root"
              popperModifiers={[
                offset(8),
                shift({
                  limiter: limitShift(),
                }),
              ]}
            />
          )}
        />
      </div>

      {/* ======================================================
          REGION
          
          Independent:
          Shows ALL regions initially.

          Dependent:
          When selected, fetches filtered Territories.
      ====================================================== */}

      <div className="col mb-2 state-field">
        <Controller
          name="region"
          control={control}
          defaultValue=""
          render={({ field }) => {
            const regionOptions: Option[] =
              region_dropdown?.map(
                (item: string) => ({
                  label: item,
                  value: item,
                })
              ) ?? [];

            const selectedRegions: Option[] =
              field.value
                ? String(field.value)
                    .split("&")
                    .filter(Boolean)
                    .map((value) => ({
                      label: value,
                      value,
                    }))
                : [];

            return (
              <Select<
                Option,
                true,
                GroupBase<Option>
              >
                id="region"
                placeholder="Region"
                isClearable={false}
                isSearchable
                isMulti
                closeMenuOnSelect={false}
                maxMenuHeight={220}
                styles={{
                  valueContainer: (
                    base
                  ) => ({
                    ...base,
                    maxHeight: "100px",
                    overflowY: "auto",
                  }),
                }}
                options={regionOptions}
                value={selectedRegions}
                onChange={(
                  selectedOptions
                ) => {
                  const values =
                    selectedOptions.map(
                      (option) =>
                        option.value
                    );

                  field.onChange(
                    values.join("&")
                  );

                  /*
                   * Region changed.
                   *
                   * Clear previous Territory and State
                   * selections because they may no longer
                   * belong to selected Region.
                   */
                  setValue(
                    "territory",
                    ""
                  );

                  setValue("state", "");

                  if (
                    values.length > 0
                  ) {
                    /*
                     * REGION SELECTED
                     *
                     * Filter Territory.
                     *
                     * State remains independent/all states
                     * until user selects Territory.
                     */
                    updateTerritoryDropdown(
                      []
                    );

                    updateStateDropdown(
                      state_dropdown ?? []
                    );

                    territoryDropdownMutate(
                      {},
                      {},
                      {
                        ...defaultQueryParams,
                        region: values,
                      }
                    );
                  } else {
                    /*
                     * NO REGION SELECTED
                     *
                     * Restore ALL Territories
                     * and ALL States.
                     */
                    updateTerritoryDropdown(
                      territory_dropdown ?? []
                    );

                    updateStateDropdown(
                      state_dropdown ?? []
                    );
                  }
                }}
                onBlur={field.onBlur}
                components={{
                  MultiValue:
                    MultiValueWithTooltip,
                }}
              />
            );
          }}
        />
      </div>

      {/* ======================================================
          TERRITORY

          Independent:
          Shows ALL territories when Region is empty.

          Dependent:
          When Region selected -> filtered by Region.
          When Territory selected -> fetch States.
      ====================================================== */}

      <div className="col mb-2 filter-col">
        <Controller
          name="territory"
          control={control}
          defaultValue=""
          render={({ field }) => {
            const territoryOptions: Option[] =
              territoryDropdown?.map(
                (item: string) => ({
                  label: item,
                  value: item,
                })
              ) ?? [];

            const selectedTerritories: Option[] =
              field.value
                ? String(field.value)
                    .split("&")
                    .filter(Boolean)
                    .map((value) => ({
                      label: value,
                      value,
                    }))
                : [];

            return (
              <Select<
                Option,
                true,
                GroupBase<Option>
              >
                id="territory"
                placeholder="Territory"
                isClearable={false}
                isSearchable
                isMulti
                closeMenuOnSelect={false}
                maxMenuHeight={220}
                styles={{
                  valueContainer: (
                    base
                  ) => ({
                    ...base,
                    maxHeight: "100px",
                    overflowY: "auto",
                  }),
                }}
                options={
                  territoryOptions
                }
                value={
                  selectedTerritories
                }
                onChange={(
                  selectedOptions
                ) => {
                  const values =
                    selectedOptions.map(
                      (option) =>
                        option.value
                    );

                  field.onChange(
                    values.join("&")
                  );

                  /*
                   * Territory changed,
                   * so clear old State selection.
                   */
                  setValue("state", "");

                  if (
                    values.length > 0
                  ) {
                    /*
                     * TERRITORY SELECTED
                     *
                     * Fetch filtered States.
                     */
                    updateStateDropdown([]);

                    stateDropdownMutate(
                      {},
                      {},
                      {
                        ...defaultQueryParams,
                        territory: values,
                      }
                    );
                  } else {
                    /*
                     * NO TERRITORY SELECTED
                     *
                     * Restore ALL States.
                     */
                    updateStateDropdown(
                      state_dropdown ?? []
                    );
                  }
                }}
                onBlur={field.onBlur}
                components={{
                  MultiValue:
                    MultiValueWithTooltip,
                }}
              />
            );
          }}
        />
      </div>

      {/* ======================================================
          STATE

          Independent:
          Can always be selected by itself.

          Dependent:
          If Territory selected, list is filtered by Territory.
      ====================================================== */}

      <div className="col state-field mb-2">
        <Controller
          name="state"
          control={control}
          defaultValue=""
          render={({ field }) => {
            const stateOptions: Option[] =
              stateDropdown?.map(
                (item: string) => ({
                  label: item,
                  value: item,
                })
              ) ?? [];

            const selectedStates: Option[] =
              field.value
                ? String(field.value)
                    .split("&")
                    .filter(Boolean)
                    .map((value) => ({
                      label: value,
                      value,
                    }))
                : [];

            return (
              <Select<
                Option,
                true,
                GroupBase<Option>
              >
                id="state"
                placeholder="State"
                isClearable={false}
                isSearchable
                isMulti
                closeMenuOnSelect={false}
                maxMenuHeight={220}
                styles={{
                  valueContainer: (
                    base
                  ) => ({
                    ...base,
                    maxHeight: "100px",
                    overflowY: "auto",
                  }),
                }}
                options={stateOptions}
                value={selectedStates}
                onChange={(
                  selectedOptions
                ) => {
                  const values =
                    selectedOptions.map(
                      (option) =>
                        option.value
                    );

                  field.onChange(
                    values.join("&")
                  );
                }}
                onBlur={field.onBlur}
                components={{
                  MultiValue:
                    MultiValueWithTooltip,
                }}
              />
            );
          }}
        />
      </div>

      {/* ======================================================
          RSD
      ====================================================== */}

      <div className="col mb-2 filter-col">
        <Controller
          name="rsd"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <Select<
              Option,
              true,
              GroupBase<Option>
            >
              id="rsd"
              placeholder="RSD"
              isClearable={false}
              isSearchable
              isMulti
              closeMenuOnSelect={false}
              maxMenuHeight={220}
              styles={{
                valueContainer: (
                  base
                ) => ({
                  ...base,
                  maxHeight: "100px",
                  overflowY: "auto",
                }),
              }}
              options={
                rsd_dropdown?.map(
                  (item) => ({
                    label: item.name,
                    value:
                      item.id.toString(),
                  })
                ) ?? []
              }
              value={resolveMultiSelectValue(
                field.value,
                rsd_dropdown
              )}
              onChange={(
                selectedOptions
              ) => {
                field.onChange(
                  selectedOptions
                );

                const values =
                  selectedOptions.map(
                    (option) =>
                      option.value
                  );

                resetField("cas");

                updateCasDropdown([]);

                fetchCasDropdown(values);
              }}
              onBlur={field.onBlur}
              components={{
                MultiValue:
                  MultiValueWithTooltip,
              }}
            />
          )}
        />
      </div>

      {/* ======================================================
          CAS
      ====================================================== */}

      <div className="col mb-2 filter-col">
        <Controller
          name="cas"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <Select<
              Option,
              true,
              GroupBase<Option>
            >
              id="cas"
              placeholder="CAS"
              isClearable={false}
              isSearchable
              isMulti
              closeMenuOnSelect={false}
              maxMenuHeight={220}
              styles={{
                valueContainer: (
                  base
                ) => ({
                  ...base,
                  maxHeight: "100px",
                  overflowY: "auto",
                }),
              }}
              options={
                casDropdown?.map(
                  (item) => ({
                    label: item.name,
                    value:
                      item.id.toString(),
                  })
                ) ?? []
              }
              value={resolveMultiSelectValue(
                field.value,
                casDropdown
              )}
              onChange={(
                selectedOptions
              ) => {
                field.onChange(
                  selectedOptions
                );
              }}
              onBlur={field.onBlur}
              components={{
                MultiValue:
                  MultiValueWithTooltip,
              }}
            />
          )}
        />
      </div>

      {/* ======================================================
          PRACTICE
      ====================================================== */}

      <div className="col mb-2 filter-col">
        <Controller
          name="practice_id"
          control={control}
          render={({ field }) => (
            <Select
              options={
                (practiceListing &&
                practiceListing.length > 0
                  ? practiceListing.map(
                      (item) => ({
                        value:
                          item.id.toString(),
                        label: item.name,
                      })
                    )
                  : []) as unknown as OptionsOrGroups<
                  string,
                  GroupBase<string>
                >
              }
              id="practice_id"
              placeholder="Practice"
              isSearchable
              isClearable
              {...field}
              noOptionsMessage={() =>
                "Please enter 2 or more characters to search..."
              }
              onInputChange={
                debouncedFilterPractice
              }
              components={{
                SingleValue:
                  SingleValueWithTooltip,
              }}
            />
          )}
        />
      </div>

      {/* ======================================================
          PRESCRIBER
      ====================================================== */}

      <div className="col mb-2 filter-col">
        <Controller
          name="global_doctor_id"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <Select
              isClearable
              options={
                (providersListing &&
                providersListing.length > 0
                  ? providersListing.map(
                      (item) => ({
                        value:
                          item.id.toString(),
                        label: item.name,
                      })
                    )
                  : []) as unknown as OptionsOrGroups<
                  string,
                  GroupBase<string>
                >
              }
              id="global_doctor_id"
              placeholder="Prescriber"
              isSearchable
              {...field}
              noOptionsMessage={() =>
                "Please enter 2 or more characters to search..."
              }
              onInputChange={
                debouncedFilterDoctor
              }
              components={{
                SingleValue:
                  SingleValueWithTooltip,
              }}
            />
          )}
        />
      </div>

      {/* ======================================================
          PATIENT ID
      ====================================================== */}

      <div className="col id-field">
        <Controller
          name="global_patient_id"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <HubInputField
              inputId="global_patient_id"
              isRowBased
              isRequired={false}
              field={field}
              inputPlaceholder="Pt. ID"
            />
          )}
        />
      </div>

      {/* ======================================================
          CASE ID
      ====================================================== */}

      <div className="col id-field">
        <Controller
          name="case_id"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <HubInputField
              inputId="case_id"
              isRowBased
              isRequired={false}
              field={field}
              inputPlaceholder="Case ID"
            />
          )}
        />
      </div>

      {/* ======================================================
          BUTTONS
      ====================================================== */}

      <div className="col btn-col mb-2">
        <div className="row g-2">
          <div className="col-6">
            <button
              type="submit"
              className="btn btn-primary w-100 rounded"
            >
              Search
            </button>
          </div>

          <div className="col-6">
            <button
              type="button"
              onClick={clearFormData}
              className="btn btn-outline-secondary w-100 rounded"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ActionDashBoardFilter;